import {
  ColDef,
  ValueGetterParams
} from 'ag-grid-community';
import BooleanRenderer from '../components/CheckmateGridComponents/CheckmateBooleanRenderer';
import CheckmateEmailLinkRenderer from '../components/CheckmateGridComponents/CheckmateEmailLinkRenderer';
import CheckmateLinkRenderer from '../components/CheckmateGridComponents/CheckmateLinkRenderer';
import CheckmateMapLinkRenderer from '../components/CheckmateGridComponents/CheckmateMapLinkRenderer';
import CheckmateStringRenderer from '../components/CheckmateGridComponents/CheckmateStringRenderer';
import TaxonomyTypeRenderer from '../components/CheckmateGridComponents/CheckmateTaxonomyTypeRenderer';
import DeleteActionRenderer from '../components/CheckmateGridComponents/DeleteActionRenderer';
import { formatDate, formatUnixDate } from './transform';
import ReportTypeRenderer from '../components/CheckmateGridComponents/CheckmateReportTypeRenderer';

const defaultColumnDef: ColDef = {
  resizable: true
}

const StringColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agTextColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    cellRenderer: CheckmateStringRenderer,
    // valueGetter: (params: ValueGetterParams): string => {
    //   if (Array.isArray(params.data[fieldName])) {
    //     return params.data[fieldName].join(', ')
    //   }
    //   return params.data[fieldName];
    // }
  };
};

const NumberColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  const colDef: ColDef = {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agNumberColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    type: 'rightAligned'
  };
  return colDef;
};

const DateColumnDef = (
  fieldName: string,
  label: string,
  format: string,
  enableFilter: boolean,
  width: number = 200,
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agDateColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    comparator: dateSortComparator(format),
    filterParams: {
      comparator: dateFilterComparator(format)
    },
    valueGetter: dateFormatter(format, fieldName)
  };
};

const TaxonomyTypeColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    sortable: enableFilter,
    filter: enableFilter ? 'agSetFilter' : '',
    initialWidth: width,
    suppressSizeToFit: true,
    suppressAutoSize: true,
    cellRenderer: TaxonomyTypeRenderer
  }
}

const ReportTypeColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    sortable: enableFilter,
    filter: enableFilter ? 'agSetFilter' : '',
    initialWidth: width,
    cellRenderer: ReportTypeRenderer
  }
}

const LinkColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agTextColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    cellRenderer: CheckmateLinkRenderer
  };
};

const MapLinkColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agTextColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    cellRenderer: CheckmateMapLinkRenderer
  };
};

const EmailLinkColumnDef = (
  fieldName: string,
  label: string,
  enableFilter: boolean,
  width: number = 200
): ColDef => {
  return {
    ...defaultColumnDef,
    field: fieldName,
    headerName: label,
    filter: enableFilter ? 'agTextColumnFilter' : '',
    sortable: enableFilter,
    initialWidth: width,
    cellRenderer: CheckmateEmailLinkRenderer
  };
};

const BooleanColumnDef = (
  fieldName: string,
  label: string,
  width: number = 120
): ColDef => {
  return {
    field: fieldName,
    headerName: label,
    sortable: false,
    filter: false,
    cellRenderer: BooleanRenderer,
    width: width,
    maxWidth: width,
    minWidth: width,
    suppressMenu: true
  }
}

const DeleteColumnDef = (
  fieldName: string,
  label: string,
  width: number = 60
) => ({
    field: fieldName,
    headerName: label,
    width: width,
    maxWidth: width,
    minWidth: width,
    suppressMenu: true,
    sortable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: DeleteActionRenderer
})

/**
 * Parses an Intl-formatted "MM/DD/YYYY" or "MM/DD/YYYY HH:mm A" string into a Date.
 * Strips the time portion (and any comma separator) before splitting on "/" so that
 * dateTimeFormat cell values like "06/03/2026, 02:30 PM" parse correctly.
 */
const parseMDY = (str: string): Date => {
  // Strip optional time portion: ", HH:MM AM/PM" or " HH:MM AM/PM"
  const datePart = str.replace(/,?\s+\d{1,2}:\d{2}\s*(AM|PM)$/i, '');
  const [m, d, y] = datePart.split('/');
  return new Date(+y, +m - 1, +d);
};

const dateSortComparator = (_format: string) => (arg1: string, arg2: string) => {
  if (!arg1 && !arg2) return 0;
  if (!arg1) return -1;
  if (!arg2) return 1;
  const t1 = parseMDY(arg1).getTime();
  const t2 = parseMDY(arg2).getTime();
  return t1 === t2 ? 0 : t1 > t2 ? 1 : -1;
};

const dateFilterComparator = (_format: string) => (filterLocalDateAtMidnight: Date, cellValue: string) => {
  if (!cellValue) return -1;
  const cellDate = parseMDY(cellValue);
  if (isNaN(cellDate.getTime())) return -1;
  if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) return 0;
  if (cellDate < filterLocalDateAtMidnight) return -1;
  if (cellDate > filterLocalDateAtMidnight) return 1;
  return 0;
};

const dateFormatter =
  (format: string, fieldName: string) => (params: ValueGetterParams) => {
    if (Array.isArray(params.data[fieldName]) && params.data[fieldName]?.length) {
      return params.data[fieldName].map((value) => {
        return parseIndividualDate(value, format);
      })
    } else if (params.data[fieldName]) {
      return parseIndividualDate(params.data[fieldName], format)
    } else {
      return '';
    }
  };

const parseIndividualDate = (value: any, format: string) => {
  if ((typeof value == 'number' || parseInt(value)) && value > 100000) {
    return formatUnixDate(value, format)
  } else if (typeof value === 'string' && value?.length > 5) {
    return formatDate(value, format);
  } else {
    return value;
  }
}

export {
  TaxonomyTypeColumnDef,
  DateColumnDef,
  NumberColumnDef,
  StringColumnDef,
  BooleanColumnDef,
  LinkColumnDef,
  MapLinkColumnDef,
  EmailLinkColumnDef,
  DeleteColumnDef,
  ReportTypeColumnDef,
  // Exported for unit testing only
  dateSortComparator,
  dateFilterComparator,
};